import React, { useEffect, useState } from 'react';
import {
  Container, Typography, Button, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, CircularProgress, Alert, Snackbar,
  IconButton, Menu, MenuItem
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { getServiceTypesList, createServiceType, deleteServiceType } from '../../services/serviceTypeService';
import AddServiceTypeDialog from '../../components/forms/AddServiceTypeDialog';
import EditServiceTypeDialog from '../../components/forms/Edit_forms/EditServiceTypeDialog';
import { useAuth } from '../../contexts/AuthContext';

function flattenErrors(obj, prefix = '') {
  let messages = [];
  for (const [key, value] of Object.entries(obj)) {
    const label = prefix ? `${prefix}.${key}` : key;
    if (Array.isArray(value)) {
      messages.push(`${label}: ${value.join(', ')}`);
    } else if (typeof value === 'object' && value !== null) {
      messages = messages.concat(flattenErrors(value, label));
    } else {
      messages.push(`${label}: ${value}`);
    }
  }
  return messages;
}

const ServiceTypesPage = () => {
  const [serviceTypes, setServiceTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const { user } = useAuth();

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const fetchServiceTypes = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getServiceTypesList();
      setServiceTypes(data);
    } catch (e) {
      setError('Failed to load service types list.');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchServiceTypes();
  }, []);

  const handleAddServiceType = async (form) => {
    try {
      await createServiceType(form);
      setSnackbar({ open: true, message: 'Service type added successfully!', severity: 'success' });
      setAddOpen(false);
      fetchServiceTypes();
    } catch (err) {
      const msg = flattenErrors(err.response?.data || {})?.join(' | ') || 'Failed to add service type';
      setSnackbar({ open: true, message: msg, severity: 'error' });
      throw new Error(msg);
    }
  };

  // const handleEditServiceType = async (form) => {
  //   try {
  //     await updateServiceType(selected.id || selected.type_id, form);
  //     setSnackbar({ open: true, message: 'Service type updated.', severity: 'success' });
  //     setEditOpen(false);
  //     fetchServiceTypes();
  //   } catch (err) {
  //     setSnackbar({ open: true, message: 'Failed to update.', severity: 'error' });
  //   }
  // };

  const handleDeleteServiceType = async () => {
    if (!selected) return;
    const confirm = window.confirm(`Delete service type "${selected.type_description}"?`);
    if (!confirm) return;

    try {
      await deleteServiceType(selected.id || selected.type_id);
      setSnackbar({ open: true, message: 'Service type deleted.', severity: 'success' });
      fetchServiceTypes();
    } catch (err) {
      setSnackbar({ open: true, message: 'Delete failed.', severity: 'error' });
    }
  };

  const handleMenuOpen = (e, item) => {
    setSelected(item);
    setMenuAnchorEl(e.currentTarget);
  };

  const handleMenuClose = () => setMenuAnchorEl(null);

  return (
    <Container>
      <Typography variant="h4" gutterBottom>Service Types</Typography>
      <Button variant="contained" color="primary" sx={{ mb: 2 }} onClick={() => setAddOpen(true)}>Add Service Type</Button>

      <Paper sx={{ p: 2 }}>
        {loading ? (
          <CircularProgress />
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Time Cost</TableCell>
                  <TableCell>Charge</TableCell>
                  {user?.role === 'admin' && <TableCell>Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {serviceTypes.map((s) => (
                  <TableRow key={s.id || s.type_id}>
                    <TableCell>{s.id || s.type_id}</TableCell>
                    <TableCell>{s.type_description}</TableCell>
                    <TableCell>{s.time_cost}</TableCell>
                    <TableCell>{s.charge}</TableCell>
                    {user?.role === 'admin' && (
                      <TableCell>
                        <IconButton onClick={(e) => handleMenuOpen(e, s)}>
                          <VisibilityIcon />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={() => { setEditOpen(true); handleMenuClose(); }}>Edit</MenuItem>
        <MenuItem onClick={() => { handleDeleteServiceType(); handleMenuClose(); }}>Delete</MenuItem>
      </Menu>

      <AddServiceTypeDialog open={addOpen} onClose={() => setAddOpen(false)} onAdd={handleAddServiceType} />
      <EditServiceTypeDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        onEdit={fetchServiceTypes} // Re-fetch data after update
        initialData={selected}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ServiceTypesPage;
